import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Camera, Plus, X, Save, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const SPORTS_LIST = [
  "Pickleball", "Tennis", "Running", "Cycling", "Golf", "Hiking", "Yoga",
  "CrossFit / Functional Fitness", "Soccer", "Basketball", "Softball", "Baseball",
  "Football/Flag Football", "Swimming", "Volleyball", "Skiing/Snowboarding",
  "Bowling", "Rock Climbing", "Martial Arts/MMA", "Ice Hockey", "Lacrosse",
  "Rugby", "Padel", "Racquetball", "Table Tennis (Ping Pong)", "Badminton",
  "Squash", "Gymnastics", "Motorsport", "Cricket", "Field Hockey",
  "Cheerleading", "Equestrian"
];

const PERFORMANCE_LEVELS = [
  { value: "beginner", label: "Beginner", color: "bg-green-500" },
  { value: "intermediate", label: "Intermediate", color: "bg-blue-500" },
  { value: "advanced", label: "Advanced", color: "bg-yellow-500" },
  { value: "elite", label: "Elite", color: "bg-orange-500" },
  { value: "college_pro", label: "College/Pro", color: "bg-red-500" }
];

const PROMPTS = {
  personality: [
    "Let's make sure we…", "Here is a POV I have -…", "On my days off …",
    "My pet peeve(s)…", "I'm overly competitive about…",
    "You should not go out with me if…", "My best adventure was…"
  ],
  conversation: [
    "Truth or dare?", "My favorite sport is…", "My favorite trashtalk line is…",
    "When I win I…", "When I lose I…", "Get to know me by…",
    "We'll get along if…"
  ],
  values: [
    "I get excited about…", "Something I'm currently obsessed with…",
    "My most irrational fear…", "I'm known for…", "My passions are…"
  ],
  lifestyle: [
    "A goal of mine…", "My ideal weekend…", "I recently learned...",
    "My bucket list includes…", "A typical day in my life…"
  ]
};

export const ProfileEdit = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    height: '',
    location: '',
    dating_intentions: '',
    work: '',
    job_title: '',
    school: '',
    sports: [],
    photos: [],
    prompts: []
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.get();
      setProfile({
        bio: response.data.bio || '',
        height: response.data.height || '',
        location: response.data.location || '',
        dating_intentions: response.data.dating_intentions || '',
        work: response.data.work || '',
        job_title: response.data.job_title || '',
        school: response.data.school || '',
        sports: response.data.sports || [],
        photos: response.data.photos || [],
        prompts: response.data.prompts || []
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && profile.photos.length < 6) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile(prev => ({
          ...prev,
          photos: [...prev.photos, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (index) => {
    setProfile(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const addSport = () => {
    if (profile.sports.length < 3) {
      setProfile(prev => ({
        ...prev,
        sports: [...prev.sports, { sport: '', level: 'beginner' }]
      }));
    }
  };

  const updateSport = (index, field, value) => {
    setProfile(prev => ({
      ...prev,
      sports: prev.sports.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeSport = (index) => {
    setProfile(prev => ({
      ...prev,
      sports: prev.sports.filter((_, i) => i !== index)
    }));
  };

  const removePrompt = (index) => {
    setProfile(prev => ({
      ...prev,
      prompts: prev.prompts.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await profileAPI.update(profile);
      toast.success('Profile updated successfully!');
      if (refreshUser) await refreshUser();
      navigate('/profile');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/profile')}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="font-display font-bold text-xl uppercase text-gray-900">Edit Profile</h1>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-primary hover:bg-red-600"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      <main className="pt-20 px-4">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Photos Section */}
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase text-gray-900">Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {profile.photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                    <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
                {profile.photos.length < 6 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <Camera className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-400">Add Photo</span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
            </CardContent>
          </Card>

          {/* Bio Section */}
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase text-gray-900">About Me</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="font-body text-gray-600">Bio</Label>
                <Textarea
                  placeholder="Tell others about yourself..."
                  className="min-h-[100px] bg-white border-gray-200"
                  value={profile.bio}
                  onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Sports Section */}
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-lg uppercase text-gray-900">Sports</CardTitle>
              {profile.sports.length < 3 && (
                <Button variant="ghost" size="sm" onClick={addSport}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {profile.sports.map((sport, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1 space-y-2">
                    <Select
                      value={sport.sport}
                      onValueChange={(v) => updateSport(index, 'sport', v)}
                    >
                      <SelectTrigger className="bg-white border-gray-200">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORTS_LIST.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={sport.level}
                      onValueChange={(v) => updateSport(index, 'level', v)}
                    >
                      <SelectTrigger className="bg-white border-gray-200">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERFORMANCE_LEVELS.map(l => (
                          <SelectItem key={l.value} value={l.value}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${l.color}`} />
                              {l.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeSport(index)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {profile.sports.length === 0 && (
                <p className="text-center text-gray-400 py-4">No sports added yet</p>
              )}
            </CardContent>
          </Card>

          {/* Basic Info Section */}
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase text-gray-900">Basic Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-gray-600">Height</Label>
                  <Input
                    placeholder="5'10&quot;"
                    className="bg-white border-gray-200"
                    value={profile.height}
                    onChange={(e) => setProfile(prev => ({ ...prev, height: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-gray-600">Location</Label>
                  <Input
                    placeholder="City, State"
                    className="bg-white border-gray-200"
                    value={profile.location}
                    onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-body text-gray-600">Dating Intentions</Label>
                <Select
                  value={profile.dating_intentions}
                  onValueChange={(v) => setProfile(prev => ({ ...prev, dating_intentions: v }))}
                >
                  <SelectTrigger className="bg-white border-gray-200">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Long-term relationship">Long-term relationship</SelectItem>
                    <SelectItem value="Short-term relationship">Short-term relationship</SelectItem>
                    <SelectItem value="Something casual">Something casual</SelectItem>
                    <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    <SelectItem value="New friends">New friends</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Work & Education */}
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase text-gray-900">Work & Education</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="font-body text-gray-600">Company</Label>
                <Input
                  data-testid="work-input"
                  placeholder="Where do you work?"
                  className="bg-white border-gray-200"
                  value={profile.work}
                  onChange={(e) => setProfile(prev => ({ ...prev, work: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-gray-600">Job Title</Label>
                <Input
                  data-testid="job-title-input"
                  placeholder="What's your role?"
                  className="bg-white border-gray-200"
                  value={profile.job_title}
                  onChange={(e) => setProfile(prev => ({ ...prev, job_title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-gray-600">School</Label>
                <Input
                  data-testid="school-input"
                  placeholder="Where did you study?"
                  className="bg-white border-gray-200"
                  value={profile.school}
                  onChange={(e) => setProfile(prev => ({ ...prev, school: e.target.value }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Prompts Section */}
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-lg uppercase text-gray-900">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Prompts
                </div>
              </CardTitle>
              <span className="font-body text-sm text-gray-500">{profile.prompts?.length || 0}/3</span>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Prompts */}
              {profile.prompts?.map((prompt, index) => (
                <div key={index} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-body text-sm text-gray-500">{prompt.prompt}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePrompt(index)}
                      className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="font-body text-gray-700">{prompt.answer}</p>
                </div>
              ))}

              {/* Add New Prompt */}
              {(profile.prompts?.length || 0) < 3 && (
                <PromptAdder 
                  prompts={PROMPTS}
                  onAdd={(prompt) => setProfile(prev => ({
                    ...prev,
                    prompts: [...(prev.prompts || []), prompt]
                  }))}
                />
              )}

              {profile.prompts?.length === 0 && (
                <p className="text-center text-gray-400 py-2">Add prompts to showcase your personality</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

// Prompt Adder Component
const PromptAdder = ({ prompts, onAdd }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [answer, setAnswer] = useState('');

  const categoryLabels = {
    personality: 'Personality',
    conversation: 'Conversation',
    values: 'Values',
    lifestyle: 'Lifestyle'
  };

  const handleAdd = () => {
    if (selectedPrompt && answer.trim()) {
      onAdd({ prompt: selectedPrompt, answer: answer.trim() });
      setSelectedCategory('');
      setSelectedPrompt('');
      setAnswer('');
    }
  };

  return (
    <div className="space-y-3 p-4 rounded-xl bg-gray-50 border border-dashed border-gray-200">
      <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setSelectedPrompt(''); }}>
        <SelectTrigger data-testid="prompt-category-select" className="bg-white border-gray-200">
          <SelectValue placeholder="Select a category" />
        </SelectTrigger>
        <SelectContent>
          {Object.keys(prompts).map((cat) => (
            <SelectItem key={cat} value={cat}>{categoryLabels[cat]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedCategory && (
        <Select value={selectedPrompt} onValueChange={setSelectedPrompt}>
          <SelectTrigger data-testid="prompt-select" className="bg-white border-gray-200">
            <SelectValue placeholder="Select a prompt" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {prompts[selectedCategory]?.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {selectedPrompt && (
        <>
          <Textarea
            data-testid="prompt-answer-input"
            placeholder="Your answer..."
            className="bg-white border-gray-200"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
          <Button 
            data-testid="add-prompt-btn"
            size="sm" 
            onClick={handleAdd}
            disabled={!answer.trim()}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Prompt
          </Button>
        </>
      )}
    </div>
  );
};
